import { chain, defaults, map, omit } from 'lodash';
import {
  COMPARISON_TABLE_PROPS,
  SUMMARY_REPORT_TABLE_PROPS,
  X_AXIS_LABEL,
  Y_AXIS_LABEL,
} from '@/summaryReport/summaryData.constants';
import { COMMON_PROPS, type PROPS_TO_DEHYDRATE } from '@/trendData/baseItem.constants';
import { BaseItemStore } from '@/trendData/baseItem.store';
import { ITEM_DATA_STATUS, ITEM_TYPES } from '@/trendData/trendData.constants';
import type { ChartPoint, StatisticsSummaryItem } from '@/summaryReport/summaryData.types';

interface SummaryReportState {
  items: StatisticsSummaryItem[];
  baselineId: string | null;
  selectedPoints: ChartPoint[];
  isItemsOverlaid: boolean;
  showHistogram: boolean;
  showBoxPlot: boolean;
  showTable: boolean;
  showBellCurve: boolean;
  status: ITEM_DATA_STATUS;
  xAxisLabel: X_AXIS_LABEL;
  yAxisLabel: Y_AXIS_LABEL;
  showSpecificationLimits: boolean;
  showBoxplotPoints: boolean;
  visibleStats: string[];
  showSignal: boolean;
  showTimeRange: boolean;
  showCondition: boolean;
  showControlCharts: boolean;
  showSigmaLevels: boolean;
  showControlLimits: boolean;
  showRunRules: boolean;
  showRunRuleViolations: boolean;
  showIssues: boolean;
}

type SummaryReportDehydratedItem = Pick<
  StatisticsSummaryItem,
  Extract<(typeof PROPS_TO_DEHYDRATE)[number], keyof StatisticsSummaryItem>
> & {
  selected?: boolean;
  autoDisabled?: boolean;
};

export type SummaryReportDehydratedState = Omit<SummaryReportState, 'status' | 'items'> & {
  items: SummaryReportDehydratedItem[];
};

export class SummaryReportStore extends BaseItemStore<StatisticsSummaryItem> {
  static readonly storeName = 'sqSummaryReportStore';

  initialize(): void {
    const initialState: SummaryReportState = defaults(
      {
        items: [],
        baselineId: null,
        isItemsOverlaid: false,
        showHistogram: true,
        showBoxPlot: true,
        showTable: true,
        showBellCurve: true,
        showControlCharts: true,
        status: ITEM_DATA_STATUS.INITIALIZING,
        xAxisLabel: X_AXIS_LABEL.UNIT,
        yAxisLabel: Y_AXIS_LABEL.COUNT,
        selectedPoints: [],
        showSpecificationLimits: true,
        showBoxplotPoints: true,
        showSigmaLevels: false,
        showControlLimits: true,
        visibleStats: [...SUMMARY_REPORT_TABLE_PROPS, ...COMPARISON_TABLE_PROPS].map((prop) => prop.key).sort(),
        showSignal: true,
        showTimeRange: true,
        showCondition: true,
        showRunRules: true,
        showRunRuleViolations: true,
        showIssues: true,
      },
      COMMON_PROPS,
    );
    this.state = this.immutable(initialState);
  }

  get items(): StatisticsSummaryItem[] {
    return this.state.get('items');
  }

  get baselineId(): string | null {
    return this.state.get('baselineId');
  }

  get baseline(): StatisticsSummaryItem | null {
    const id = this.baselineId;
    if (!id) return null;
    return this.items.find((item) => item.id === id) ?? null;
  }

  get isItemsOverlaid(): boolean {
    return this.state.get('isItemsOverlaid');
  }

  get showHistogram(): boolean {
    return this.state.get('showHistogram');
  }

  get showBoxPlot(): boolean {
    return this.state.get('showBoxPlot');
  }

  get showTable(): boolean {
    return this.state.get('showTable');
  }

  get showControlCharts(): boolean {
    return this.state.get('showControlCharts');
  }

  get status(): ITEM_DATA_STATUS {
    return this.state.get('status');
  }

  get visibleStats(): string[] {
    return this.state.get('visibleStats');
  }

  get showBellCurve(): boolean {
    return this.state.get('showBellCurve');
  }

  get xAxisLabel(): X_AXIS_LABEL {
    return this.state.get('xAxisLabel');
  }

  get yAxisLabel(): Y_AXIS_LABEL {
    return this.state.get('yAxisLabel');
  }

  get showSpecificationLimits(): boolean {
    return this.state.get('showSpecificationLimits');
  }

  get showSignal(): boolean {
    return this.state.get('showSignal');
  }

  get showTimeRange(): boolean {
    return this.state.get('showTimeRange');
  }

  get showCondition(): boolean {
    return this.state.get('showCondition');
  }

  get showBoxplotPoints(): boolean {
    return this.state.get('showBoxplotPoints');
  }

  get showSigmaLevels(): boolean {
    return this.state.get('showSigmaLevels');
  }

  get showControlLimits(): boolean {
    return this.state.get('showControlLimits');
  }

  get showRunRules(): boolean {
    return this.state.get('showRunRules');
  }

  get showRunRuleViolations(): boolean {
    return this.state.get('showRunRuleViolations');
  }

  get showIssues(): boolean {
    return this.state.get('showIssues');
  }

  get selectedPoints(): ChartPoint[] {
    return this.state.get('selectedPoints');
  }

  dehydrate(): SummaryReportDehydratedState {
    return {
      items: chain(this.state.get('items'))
        .map((item) => this.pruneDehydratedItemProps(item))
        .value() as SummaryReportDehydratedItem[],
      isItemsOverlaid: this.isItemsOverlaid,
      showHistogram: this.showHistogram,
      showBoxPlot: this.showBoxPlot,
      showControlCharts: this.showControlCharts,
      showTable: this.showTable,
      visibleStats: this.visibleStats,
      showBellCurve: this.showBellCurve,
      xAxisLabel: this.xAxisLabel,
      yAxisLabel: this.yAxisLabel,
      showSignal: this.showSignal,
      showTimeRange: this.showTimeRange,
      showCondition: this.showCondition,
      baselineId: this.baselineId,
      showBoxplotPoints: this.showBoxplotPoints,
      showSpecificationLimits: this.showSpecificationLimits,
      showSigmaLevels: this.showSigmaLevels,
      showControlLimits: this.showControlLimits,
      showRunRules: this.showRunRules,
      showRunRuleViolations: this.showRunRuleViolations,
      showIssues: this.showIssues,
      selectedPoints: this.selectedPoints,
    };
  }

  async rehydrate(dehydratedState: Partial<SummaryReportDehydratedState>): Promise<void> {
    this.state.merge(omit(dehydratedState, ['items']));
    this.state.set(
      'items',
      map(dehydratedState.items || [], (item) =>
        this.createItem(item.id, item.name, ITEM_TYPES.FORMULA_ITEM as any, omit(item, ['id', 'name', 'itemType'])),
      ),
    );
  }

  setItems(payload: { items: StatisticsSummaryItem[] }): void {
    const items = payload.items?.map((item) => ({ ...item, itemType: ITEM_TYPES.FORMULA_ITEM }));
    this.state.set('items', items);
  }

  setBaseline(payload: { item: StatisticsSummaryItem | null }): void {
    this.state.set('baselineId', payload.item?.id ?? null);
  }

  updateItem(payload: { item: StatisticsSummaryItem }): void {
    const updatedItems = map(this.items, (item) => (item.id === payload.item.id ? payload.item : item));
    this.setItems({ items: updatedItems });
  }

  addItem(payload: { item: StatisticsSummaryItem }): void {
    const currentItems = this.items;
    if (!this.findItem(payload.item.id)) {
      this.setItems({ items: [...currentItems, payload.item] });
    } else {
      this.updateItem(payload);
    }
  }

  insertItemAtIndex(payload: { item: StatisticsSummaryItem; index: number }): void {
    const { item, index } = payload;
    const items = this.items ?? [];
    const without = items.filter((x: any) => x.id !== item.id);
    const clamped = Math.max(0, Math.min(index, without.length));
    const next = [...without.slice(0, clamped), item, ...without.slice(clamped)];
    this.setItems({ items: next });
  }

  setStatus(payload: { status: ITEM_DATA_STATUS }): void {
    this.state.set('status', payload.status);
  }

  setIsItemsOverlaid(payload: { isItemsOverlaid: boolean }): void {
    this.state.set('isItemsOverlaid', payload.isItemsOverlaid);
  }

  removeItems(payload: { items: StatisticsSummaryItem[] }): void {
    // Call the parent's removeItems method which handles color tracking
    super.removeItems(payload);
  }

  setShowHistogram(payload: { showHistogram: boolean }): void {
    this.state.set('showHistogram', payload.showHistogram);
  }

  setShowBoxPlot(payload: { showBoxPlot: boolean }): void {
    this.state.set('showBoxPlot', payload.showBoxPlot);
  }

  setShowTable(payload: { showTable: boolean }): void {
    this.state.set('showTable', payload.showTable);
  }

  setShowControlCharts(payload: { showControlCharts: boolean }): void {
    this.state.set('showControlCharts', payload.showControlCharts);
  }

  setVisibleStats(payload: { visibleStats: string[] }): void {
    this.state.set('visibleStats', payload.visibleStats);
  }

  setShowBellCurve(payload: { showBellCurve: boolean }): void {
    this.state.set('showBellCurve', payload.showBellCurve);
  }

  setXAxisLabel(payload: { xAxisLabel: X_AXIS_LABEL }): void {
    this.state.set('xAxisLabel', payload.xAxisLabel);
  }

  setYAxisLabel(payload: { yAxisLabel: Y_AXIS_LABEL }): void {
    this.state.set('yAxisLabel', payload.yAxisLabel);
  }

  setShowSpecificationLimits(payload: { showSpecificationLimits: boolean }): void {
    this.state.set('showSpecificationLimits', payload.showSpecificationLimits);
  }

  setShowSignal(payload: { showSignal: boolean }): void {
    this.state.set('showSignal', payload.showSignal);
  }

  setShowTimeRange(payload: { showTimeRange: boolean }): void {
    this.state.set('showTimeRange', payload.showTimeRange);
  }

  setShowCondition(payload: { showCondition: boolean }): void {
    this.state.set('showCondition', payload.showCondition);
  }

  setShowBoxplotPoints(payload: { showBoxplotPoints: boolean }): void {
    this.state.set('showBoxplotPoints', payload.showBoxplotPoints);
  }

  setShowSigmaLevels(payload: { showSigmaLevels: boolean }): void {
    this.state.set('showSigmaLevels', payload.showSigmaLevels);
  }

  setShowControlLimits(payload: { showControlLimits: boolean }): void {
    this.state.set('showControlLimits', payload.showControlLimits);
  }

  setShowRunRules(payload: { showRunRules: boolean }): void {
    this.state.set('showRunRules', payload.showRunRules);
  }

  setShowRunRuleViolations(payload: { showRunRuleViolations: boolean }): void {
    this.state.set('showRunRuleViolations', payload.showRunRuleViolations);
  }

  setShowIssues(payload: { showIssues: boolean }): void {
    this.state.set('showIssues', payload.showIssues);
  }

  setSelectedPoints(payload: { selectedPoints: ChartPoint[] }): void {
    this.state.set('selectedPoints', payload.selectedPoints);
  }

  resetView(): void {
    this.state.merge({
      showHistogram: true,
      showBoxPlot: true,
      showTable: true,
      showBellCurve: true,
      showControlCharts: true,
      showSignal: true,
      showTimeRange: true,
      showCondition: true,
      showRunRules: true,
      showRunRuleViolations: true,
      showControlLimits: true,
      showSpecificationLimits: true,
      showBoxplotPoints: true,
      showSigmaLevels: false,
      showIssues: true,
      xAxisLabel: X_AXIS_LABEL.UNIT,
      yAxisLabel: Y_AXIS_LABEL.COUNT,
    });
  }

  protected readonly handlers = {
    ...super.baseHandlers,
    SR_SET_ITEMS: this.setItems,
    SR_SET_BASELINE: this.setBaseline,
    SR_ADD_ITEM: this.addItem,
    SR_INSERT_ITEM_AT: this.insertItemAtIndex,
    SR_UPDATE_ITEM: this.updateItem,
    SR_REMOVE_ITEMS: this.removeItems,
    SR_SET_IS_ITEMS_OVERLAID: this.setIsItemsOverlaid,
    SR_SET_STATUS: this.setStatus,
    SR_SET_SHOW_HISTOGRAM: this.setShowHistogram,
    SR_SET_SHOW_BOX_PLOT: this.setShowBoxPlot,
    SR_SET_SHOW_CONTROL_CHARTS: this.setShowControlCharts,
    SR_SET_SHOW_TABLE: this.setShowTable,
    SR_SET_VISIBLE_STATS: this.setVisibleStats,
    SR_SET_SHOW_BELL_CURVE: this.setShowBellCurve,
    SR_SET_X_AXIS_LABEL: this.setXAxisLabel,
    SR_SET_Y_AXIS_LABEL: this.setYAxisLabel,
    SR_SET_SHOW_SIGNAL: this.setShowSignal,
    SR_SET_SHOW_TIME_RANGE: this.setShowTimeRange,
    SR_SET_SHOW_CONDITION: this.setShowCondition,
    SR_SET_SHOW_BOXPLOT_POINTS: this.setShowBoxplotPoints,
    SR_SET_SHOW_SPECIFICATION_LIMITS: this.setShowSpecificationLimits,
    SR_SET_SHOW_SIGMA_LEVELS: this.setShowSigmaLevels,
    SR_SET_SHOW_CONTROL_LIMITS: this.setShowControlLimits,
    SR_SET_SHOW_RUN_RULES: this.setShowRunRules,
    SR_SET_SHOW_RUN_RULE_VIOLATIONS: this.setShowRunRuleViolations,
    SR_SET_SHOW_ISSUES: this.setShowIssues,
    SR_SET_SELECTED_POINTS: this.setSelectedPoints,
    SR_RESET_VIEW: this.resetView,
  };
}
